<!-- CustomDialog.vue -->
<script setup lang="ts">
import { watch } from "vue";
import IconManager from "@/core/IconManager.vue";
import CustomButton from "@/core/components/CustomButton.vue";
import { useI18n } from "vue-i18n";
import { useScrollLock } from "@vueuse/core";

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    title?: string;
    cancelText?: string;
    submitText?: string;
    isSubmitting?: boolean;
    disableSubmitButton?: boolean;
    closeOnClickOutside?: boolean;
    isSubmitButtonVisible?: boolean;
  }>(),
  {
    isSubmitting: false,
    disableSubmitButton: false,
    closeOnClickOutside: true,
    isSubmitButtonVisible: true,
  },
);

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  cancel: [];
  submit: [];
  close: [];
}>();
const isLocked = useScrollLock(document.body);

const { t } = useI18n();

function close() {
  emit("update:modelValue", false);
  emit("close");
}

function cancel() {
  emit("cancel");
  close();
}

function submit() {
  emit("submit");
}

function handleBackdropClick() {
  if (props.closeOnClickOutside) close();
}

// prevent body scroll while dialog is open
watch(
  () => props.modelValue,
  (isOpen) => {
    isLocked.value = isOpen;
  },
  { immediate: true },
);
</script>

<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      @mousedown.self="handleBackdropClick"
    >
      <div
        class="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between px-6 py-4 bg-primary rounded-t-xl"
        >
          <h2 class="text-lg font-bold text-white">{{ title }}</h2>
          <button type="button" @click="close" class="text-white">
            <IconManager name="Close" />
          </button>
        </div>

        <!-- Body -->
        <div class="px-6 py-4 overflow-y-auto flex-1">
          <slot></slot>
        </div>

        <!-- Footer -->
        <div class="flex justify-end gap-2 px-6 py-4 border-t border-gray-200">
          <CustomButton
            @click="cancel"
            :title="cancelText ?? t('common.cancel')"
            :disabled="isSubmitting"
            button-styles="bg-white! text-gray-600!"
          />

          <CustomButton
            v-if="isSubmitButtonVisible"
            @click="submit"
            :title="submitText ?? t('common.continue')"
            :disabled="isSubmitting || disableSubmitButton"
            :isLoading="isSubmitting"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>
